import Cookies from 'js-cookie';
import type { Candidate } from '@/types/search';
import type { PublicCandidateAuthState, PublicCandidateApiResponse } from '@/types/public';

/**
 * Decode JWT token (client-side only, no verification)
 */
function decodeToken(token: string): { exp?: number; organisationId?: string } | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  const token = Cookies.get('token');
  if (!token) return false;

  const decoded = decodeToken(token);
  if (!decoded) return false;

  if (decoded.exp && decoded.exp * 1000 < Date.now()) {
    return false;
  }

  return true;
}

/**
 * Determine authentication state from API response
 */
export function determineAuthState(response: PublicCandidateApiResponse): PublicCandidateAuthState {
  switch (response.type) {
    case 'SUCCESS':
      if (!response.data) {
        return {
          type: 'ERROR',
          error: 'Invalid response format',
        };
      }
      return {
        type: 'PUBLIC',
        candidate: response.data,
      };

    case 'AUTH_REQUIRED':
      return {
        type: 'AUTH_REQUIRED',
        candidateCode: response.candidateCode || '',
        jobId: undefined, // jobId will be preserved in hook
        message: response.message || 'Authentication required',
      };

    case 'ACCESS_DENIED':
      return {
        type: 'ACCESS_DENIED',
        message: response.message || 'Access denied',
        reason: response.reason || 'OTHER',
      };

    case 'TOKEN_EXPIRED':
      return {
        type: 'ACCESS_DENIED',
        message: 'Your session has expired. Please log in again.',
        reason: 'TOKEN_EXPIRED',
      };

    case 'NOT_FOUND':
      return {
        type: 'NOT_FOUND',
        message: response.message || 'Candidate not found',
      };

    case 'SERVER_ERROR':
      return {
        type: 'ERROR',
        error: response.message || 'Server error occurred',
        statusCode: response.statusCode,
      };

    case 'ERROR':
    default:
      return {
        type: 'ERROR',
        error: response.message || 'An error occurred',
        statusCode: response.statusCode,
      };
  }
}

/**
 * Extract candidate from auth state
 */
export function extractCandidate(authState: PublicCandidateAuthState): Candidate | null {
  if (authState.type === 'PUBLIC' || authState.type === 'AUTHENTICATED') {
    return authState.candidate;
  }
  return null;
}

/**
 * Get post-login redirect URL
 */
export function getPostLoginRedirect(candidateCode: string, jobId?: string): string {
  const baseUrl = `/candidate/${candidateCode}`;
  if (jobId) {
    return `${baseUrl}?jobId=${encodeURIComponent(jobId)}`;
  }
  return baseUrl;
}

